import React, { useContext, useState, useEffect } from "react";
import ProfileCard from "@Components/user/ProfileCard";
import ActivityList from "@Components/activity/ActivityList";
import SessionManager from "@Core/session";
import { PromptTypes, QueryKeys, TermsTypes, UserPromptStatus } from "@Constants";
import { MainContext } from "@Context/provider";
import { ViewMode } from "@Context/state";
import PageHelmet from "@Components/shared/PageHelmet";
import BaseModal from "@Components/shared/BaseModal";
import { Button } from "antd";
import { useHistory } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
    useGetLatestTerms,
    useGetUserAcceptedTerms,
    useGetUserPrompts,
    useHasFeature,
} from "@Components/shared/queries";
import { useCreateTermsAction, useUpdateUserPrompt } from "@Components/shared/mutations";
import UserService from "@Services/UserService";
import { IUserDetails } from "@Models/IUserDetails";
import { calculateFormCompletion } from "@Utils";
import TermsModal from "@Components/shared/TermsModal";
import { ITermPrompt } from "@Models/ITerms";

const ProfilePage: React.FC = () => {
    const { ui: uiState } = useContext(MainContext);
    const [isTermsAndConditionsModalVisible, setIsTermsAndConditionsModalVisible] = useState(false);
    const [isPrivacyPolicyModalVisible, setIsPrivacyPolicyModalVisible] = useState(false);
    const [isGraduationYearModalVisible, setIsGraduationYearModalVisible] = useState(false);
    const [userData, setUserData] = useState<IUserDetails | null>(null);
    const queryClient = useQueryClient();
    const history = useHistory();
    const { data: hasGraduationYearField } = useHasFeature("graduation-year-field");

    //**user prompts**//
    const { data: userPrompts, isFetching: isFetchingUserPrompts } = useGetUserPrompts();
    const { mutate: updateUserPrompt } = useUpdateUserPrompt();
    const { mutate: createTermsAction, isLoading: isCreatingTermsAction } = useCreateTermsAction();
    const { data: userAcceptedTerms, isLoading: loadingUserAcceptedTerms } = useGetUserAcceptedTerms("", true);
    const hasLatestPrivacyPolicyBeenAccepted = userAcceptedTerms?.userAcceptedTerms?.some(
        (term) => term.termsType === TermsTypes.PrivacyPolicy && term.acceptedOn != null
    );
    const hasLatestTermsAndConditionsBeenAccepted = userAcceptedTerms?.userAcceptedTerms?.some(
        (term) => term.termsType === TermsTypes.Terms && term.acceptedOn != null
    );

    //**terms and conditions**//
    const { data: termsAndConditionsData, isLoading: loadingTermsAndConditions } = useGetLatestTerms(TermsTypes.Terms);
    const { data: privacyData, isLoading: loadingPrivacyPolicy } = useGetLatestTerms(TermsTypes.PrivacyPolicy);
    const termsAndConditionsPrompt = userPrompts?.userPrompts?.find(
        (prompt) => prompt.promptType === PromptTypes.Terms
    );
    const privacyPolicyPrompt = userPrompts?.userPrompts?.find(
        (prompt) => prompt.promptType === PromptTypes.PrivacyPolicy
    );

    const handleAgreeTerms = (termsVersionId: string, termsPrompt: ITermPrompt) => {
        createTermsAction({ termsVersionId, accepted: true });
        updateUserPrompt({ promptId: termsPrompt.promptId, status: UserPromptStatus.Completed });
    };

    // Fetch user data for the fallback progress calculation
    useEffect(() => {
        const fetchUserData = async () => {
            const service = new UserService();
            const user = await service.getPrivate([SessionManager.getServiceUser()?.userId]);
            if (!user.hasErrors && user.value.length > 0) {
                setUserData(user.value[0]);
            }
        };

        fetchUserData();
    }, []);

    const profileProgress = calculateFormCompletion(userData, hasGraduationYearField);

    const onNavigate = async (url: string) => {
        history.push(url);
    };

    useEffect(() => {
        if (!isFetchingUserPrompts) {
            const hasTermsPrompt = !!termsAndConditionsPrompt;
            const hasGraduationYearPrompt = userPrompts?.userPrompts?.some(
                (prompt) => prompt.promptType === PromptTypes.GraduationYear
            );
            const hasPrivacyPolicyPrompt = !!privacyPolicyPrompt;

            setIsTermsAndConditionsModalVisible(hasTermsPrompt);
            setIsPrivacyPolicyModalVisible(hasPrivacyPolicyPrompt);
            setIsGraduationYearModalVisible(hasGraduationYearPrompt);
        }
    }, [userPrompts]);

    const handleDismissTermsPrompt = (prompt: ITermPrompt | undefined) => {
        if (prompt) {
            updateUserPrompt({ promptId: prompt.promptId, status: UserPromptStatus.Dismissed });
        }
    };

    const handleDismissGraduationYearModal = () => {
        const graduationYearPrompt = userPrompts?.userPrompts?.find(
            (prompt) => prompt.promptType === PromptTypes.GraduationYear
        );
        if (graduationYearPrompt) {
            updateUserPrompt({ promptId: graduationYearPrompt.promptId, status: UserPromptStatus.Dismissed });
        }
    };

    return (
        <div className="profile-page">
            <PageHelmet name={"Profile"} />
            <div id="content">
                {uiState.mode !== ViewMode.mobile ? (
                    <div className="floating-content-right floating-content">
                        <div className="floating-content-right-inner">
                            <ActivityList
                                queryKey={QueryKeys.ActivityListUser}
                                userIds={[SessionManager.getServiceUser()?.userId]}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="mobile-view">
                        <ProfileCard profileProgress={profileProgress} />
                        <ActivityList
                            queryKey={QueryKeys.ActivityListUser}
                            userIds={[SessionManager.getServiceUser()?.userId]}
                        />
                    </div>
                )}
            </div>

            {hasGraduationYearField ? (
                <BaseModal
                    visible={isGraduationYearModalVisible}
                    footer={null}
                    onClose={async () => {
                        setIsGraduationYearModalVisible(false);
                        await queryClient.invalidateQueries([QueryKeys.UserPrompts]);
                        handleDismissGraduationYearModal();
                    }}
                    title={"Please complete your account details"}
                    content={
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            Your profile progress is at {profileProgress}%
                            <Button
                                className="secondary"
                                style={{ width: "auto", maxWidth: "200px", marginTop: "20px" }}
                                onClick={async () => {
                                    setIsGraduationYearModalVisible(false);
                                    await queryClient.invalidateQueries([QueryKeys.UserPrompts]);
                                    handleDismissGraduationYearModal();
                                    onNavigate("/account#details");
                                }}
                            >
                                Go to account details
                            </Button>
                        </div>
                    }
                />
            ) : null}
            <TermsModal
                visible={isTermsAndConditionsModalVisible}
                isLoading={loadingTermsAndConditions || loadingUserAcceptedTerms}
                onClose={async () => {
                    await queryClient.invalidateQueries([QueryKeys.UserPrompts]);
                    handleDismissTermsPrompt(termsAndConditionsPrompt);
                }}
                termsContent={termsAndConditionsData?.content}
                title={"Please accept the latest terms and conditions"}
                termsVersionId={termsAndConditionsData?.termsVersionId}
                termsPrompt={termsAndConditionsPrompt}
                termsAlreadyAccepted={hasLatestTermsAndConditionsBeenAccepted}
                onAgree={handleAgreeTerms}
                isCreatingTermsAction={isCreatingTermsAction}
                showFooter={true}
            />
            <TermsModal
                visible={isPrivacyPolicyModalVisible && !isTermsAndConditionsModalVisible}
                isLoading={loadingPrivacyPolicy || loadingUserAcceptedTerms}
                onClose={async () => {
                    await queryClient.invalidateQueries([QueryKeys.UserPrompts]);
                    handleDismissTermsPrompt(privacyPolicyPrompt);
                }}
                termsContent={privacyData?.content}
                title={"Please accept the latest privacy policy"}
                termsVersionId={privacyData?.termsVersionId}
                termsPrompt={privacyPolicyPrompt}
                termsAlreadyAccepted={hasLatestPrivacyPolicyBeenAccepted}
                onAgree={handleAgreeTerms}
                isCreatingTermsAction={isCreatingTermsAction}
                showFooter={true}
            />
        </div>
    );
};

export default ProfilePage;
